import { useEffect } from "react";
import { useAuth } from "./useAuth";
import type { AuthEventListener, AuthEventName } from "../types";

export interface SessionSnapshot {
  authenticated: boolean;
  loading: boolean;
  user: ReturnType<typeof useAuth>["user"];
}

type EventHandlers = Partial<{ [E in AuthEventName]: AuthEventListener<E> }>;

/**
 * Read-only session state, plus an optional way to subscribe to lifecycle
 * events (login, logout, refresh, sessionExpired, userChanged) for side
 * effects like analytics or toasts, without pulling in the full auth API.
 *
 * ```tsx
 * useSession({ sessionExpired: () => toast.warn("Your session expired") });
 * ```
 */
export function useSession(handlers?: EventHandlers): SessionSnapshot {
  const { user, loading, authenticated, events } = useAuth();

  useEffect(() => {
    if (!handlers) return;
    const unsubscribers = (Object.keys(handlers) as AuthEventName[]).map((event) =>
      events.on(event, handlers[event] as AuthEventListener<typeof event>)
    );
    return () => unsubscribers.forEach((unsub) => unsub());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  return { user, loading, authenticated };
}
